import React from 'react';

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const variantClasses = {
  primary: 'bg-purple-100 text-purple-700 border-purple-200',
  secondary: 'bg-gray-100 text-gray-700 border-gray-200',
  success: 'bg-green-100 text-green-700 border-green-200',
  warning: 'bg-amber-100 text-amber-700 border-amber-200',
  danger: 'bg-red-100 text-red-700 border-red-200',
  info: 'bg-blue-100 text-blue-700 border-blue-200',
  neutral: 'bg-gray-50 text-gray-600 border-gray-200',
};

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-3 py-1 text-sm',
  lg: 'px-4 py-1.5 text-base',
};

export function Badge({ children, variant = 'primary', size = 'md', className = '' }: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium border ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
    >
      {children}
    </span>
  );
}

interface PriorityBadgeProps {
  priority: 'high' | 'medium' | 'low' | 'critical';
  className?: string;
}

export function PriorityBadge({ priority, className = '' }: PriorityBadgeProps) {
  const priorityConfig = {
    critical: { label: 'CRITICAL', variant: 'danger' as const },
    high: { label: 'HIGH', variant: 'warning' as const },
    medium: { label: 'MEDIUM', variant: 'info' as const },
    low: { label: 'LOW', variant: 'neutral' as const },
  };

  const config = priorityConfig[priority];

  return (
    <Badge variant={config.variant} size="sm" className={className}>
      {config.label}
    </Badge>
  );
}

interface StatusBadgeProps {
  status: string;
  className?: string;
}

export function StatusBadge({ status, className = '' }: StatusBadgeProps) {
  const statusLower = status.toLowerCase();

  let variant: 'primary' | 'success' | 'warning' | 'neutral' = 'neutral';

  if (statusLower.includes('active') || statusLower.includes('completed')) {
    variant = 'success';
  } else if (statusLower.includes('pending') || statusLower.includes('progress')) {
    variant = 'warning';
  } else if (statusLower.includes('draft')) {
    variant = 'neutral';
  }

  return (
    <Badge variant={variant} size="sm" className={className}>
      {status}
    </Badge>
  );
}

interface ConfidenceBadgeProps {
  score: number;
  className?: string;
}

export function ConfidenceBadge({ score, className = '' }: ConfidenceBadgeProps) {
  const getVariant = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 75) return 'primary';
    if (score >= 60) return 'warning';
    return 'danger';
  };

  return (
    <Badge variant={getVariant(score)} size="sm" className={className}>
      {score}% confidence
    </Badge>
  );
}

interface EffortBadgeProps {
  level: 'low' | 'medium' | 'high';
  className?: string;
}

export function EffortBadge({ level, className = '' }: EffortBadgeProps) {
  const effortConfig = {
    low: { label: 'low effort', variant: 'success' as const },
    medium: { label: 'medium effort', variant: 'warning' as const },
    high: { label: 'high effort', variant: 'danger' as const },
  };

  const config = effortConfig[level];

  return (
    <Badge variant={config.variant} size="sm" className={className}>
      {config.label}
    </Badge>
  );
}
